import { useEffect, useMemo, useState } from 'react';
import { buildMentionMarkup } from '../../../../helpers/mentions';

const INITIAL_MENTION_SEARCH = { query: '', start: -1, end: -1 };
const mentionTriggerRegex = /(?:^|\s)@([^\s@]*)$/;

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const useChatMentions = ({ members = [], message, setMessage, textareaRef }) => {
    const [selectedMentions, setSelectedMentions] = useState([]);
    const [mentionSearch, setMentionSearch] = useState(INITIAL_MENTION_SEARCH);
    const [activeMentionIndex, setActiveMentionIndex] = useState(0);

    const resetMentions = () => {
        setSelectedMentions([]);
        setMentionSearch(INITIAL_MENTION_SEARCH);
        setActiveMentionIndex(0);
    };

    const closeMentionSearch = () => {
        setMentionSearch(INITIAL_MENTION_SEARCH);
        setActiveMentionIndex(0);
    };

    const updateMentionSearch = (text, caretPosition = text.length) => {
        const textBeforeCaret = text.slice(0, caretPosition);
        const mentionMatch = textBeforeCaret.match(mentionTriggerRegex);

        if (!mentionMatch) {
            closeMentionSearch();
            return;
        }

        const query = mentionMatch[1] || '';
        const start = caretPosition - query.length - 1;

        setMentionSearch({
            query,
            start,
            end: caretPosition,
        });
        setActiveMentionIndex(0);
    };

    const serializeMentions = (text) => {
        if (!selectedMentions.length) return text;

        let serializedText = text;

        selectedMentions.forEach((mention) => {
            const mentionPattern = new RegExp(`@${escapeRegExp(mention.name)}(?![\\w-])`);
            serializedText = serializedText.replace(
                mentionPattern,
                buildMentionMarkup(mention.name, mention.userId)
            );
        });

        return serializedText;
    };

    const mentionOptions = useMemo(() => (
        members
            .filter((member) => member?.name)
            .filter((member) => member.name.toLowerCase().includes(mentionSearch.query.toLowerCase()))
    ), [members, mentionSearch.query]);

    const selectMention = (member) => {
        if (mentionSearch.start < 0) return;

        const mentionText = `@${member.name} `;
        const nextMessage = `${message.slice(0, mentionSearch.start)}${mentionText}${message.slice(mentionSearch.end)}`;
        const nextCursorPosition = mentionSearch.start + mentionText.length;

        setMessage(nextMessage);
        setSelectedMentions((prev) => [...prev, { userId: member.user_id, name: member.name }]);
        closeMentionSearch();

        requestAnimationFrame(() => {
            if (!textareaRef.current) return;

            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
        });
    };

    const handleMentionKeyDown = (event) => {
        const isMentionMenuOpen = mentionSearch.start >= 0 && mentionOptions.length > 0;

        if (!isMentionMenuOpen) {
            return false;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveMentionIndex((prev) => (prev + 1) % mentionOptions.length);
            return true;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveMentionIndex((prev) => (prev - 1 + mentionOptions.length) % mentionOptions.length);
            return true;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            closeMentionSearch();
            return true;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            selectMention(mentionOptions[activeMentionIndex]);
            return true;
        }

        return false;
    };

    useEffect(() => {
        setSelectedMentions((prev) => {
            if (!prev.length) return prev;

            const mentionUsage = new Map();

            return prev.filter((mention) => {
                const mentionPattern = new RegExp(`@${escapeRegExp(mention.name)}(?![\\w-])`, 'g');
                const matchesCount = message.match(mentionPattern)?.length || 0;
                const usedCount = mentionUsage.get(mention.userId) || 0;

                if (usedCount >= matchesCount) {
                    return false;
                }

                mentionUsage.set(mention.userId, usedCount + 1);
                return true;
            });
        });
    }, [message]);

    return {
        activeMentionIndex,
        handleMentionKeyDown,
        mentionOptions,
        mentionSearch,
        resetMentions,
        selectMention,
        serializeMentions,
        updateMentionSearch,
    };
};

export default useChatMentions;
